/**
 * usePushNotifications Hook
 *
 * Manages Web Push notification subscription lifecycle.
 *
 * NOTE: All logging uses _log/_warn/_err helpers so they survive
 * the production esbuild `drop: ['console']` setting.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const API_BASE = '/api/notifications';

/* ── logging helpers (survive esbuild drop: ['console']) ──────── */

const _noop = () => {};
const _log  = (typeof console !== 'undefined' && console.log)
    ? Function.prototype.bind.call(console.log,  console)
    : _noop;
const _warn = (typeof console !== 'undefined' && console.warn)
    ? Function.prototype.bind.call(console.warn, console)
    : _noop;
const _err  = (typeof console !== 'undefined' && console.error)
    ? Function.prototype.bind.call(console.error, console)
    : _noop;

/* ── helpers ──────────────────────────────────────────────────── */

function browserSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const out = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
    return out;
}

/** Wait for SW ready with a timeout so it never hangs forever */
function swReadyWithTimeout(ms = 10000) {
    return Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Service Worker não ficou pronto a tempo')), ms)
        ),
    ]);
}

/* ── hook ─────────────────────────────────────────────────────── */

export function usePushNotifications() {
    const [isSupported] = useState(() => browserSupported());
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [permission, setPermission] = useState(() =>
        browserSupported() ? Notification.permission : 'default'
    );
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const busyRef = useRef(false);

    /* ── check existing subscription on mount (non-blocking) ───── */
    useEffect(() => {
        if (!isSupported) {
            _log('[Push] Not supported in this browser');
            return;
        }
        _log('[Push] Supported. Permission:', Notification.permission);

        navigator.serviceWorker.getRegistration('/').then(async (reg) => {
            if (!reg) {
                _log('[Push] No service worker registered yet');
                return;
            }
            try {
                const sub = await reg.pushManager.getSubscription();
                if (sub) {
                    setSubscription(sub);
                    setIsSubscribed(true);
                    _log('[Push] Existing subscription found');
                } else {
                    _log('[Push] No existing subscription');
                }
            } catch (err) {
                _err('[Push] Error checking subscription:', err);
            }
        });
    }, [isSupported]);

    /* ── sync permission when user returns to tab ─────────────── */
    useEffect(() => {
        if (!isSupported) return;

        const sync = () => {
            const current = Notification.permission;
            setPermission((prev) => {
                if (prev !== current) _log('[Push] Permission changed:', prev, '→', current);
                return current;
            });
        };

        document.addEventListener('visibilitychange', sync);
        window.addEventListener('focus', sync);
        return () => {
            document.removeEventListener('visibilitychange', sync);
            window.removeEventListener('focus', sync);
        };
    }, [isSupported]);

    /* ── refreshPermission (callable from outside) ────────────── */
    const refreshPermission = useCallback(() => {
        if (!browserSupported()) return;
        const current = Notification.permission;
        _log('[Push] refreshPermission →', current);
        setPermission(current);
    }, []);

    /* ── subscribe ────────────────────────────────────────────── */
    const subscribe = useCallback(async () => {
        _log('[Push] subscribe() called');

        if (!browserSupported()) {
            _warn('[Push] Not supported');
            setError('Notificações push não são suportadas neste navegador');
            return false;
        }

        if (busyRef.current) {
            _log('[Push] Already busy, ignoring');
            return false;
        }
        busyRef.current = true;
        setError(null);

        try {
            // 1) Check current browser permission
            const currentPerm = Notification.permission;
            _log('[Push] Current browser permission:', currentPerm);

            if (currentPerm === 'denied') {
                _warn('[Push] Permission denied by browser');
                setPermission('denied');
                setError('Permissão bloqueada no navegador. Clique no ícone de cadeado ao lado da URL e permita notificações.');
                return false;
            }

            // 2) Request permission (MUST be first async op — preserves user gesture)
            let result;
            if (currentPerm === 'granted') {
                result = 'granted';
                _log('[Push] Already granted, skipping prompt');
            } else {
                _log('[Push] Requesting permission...');
                result = await Notification.requestPermission();
                _log('[Push] Permission result:', result);

                // Edge/Chrome "quiet notification prompts" silently dismiss and return 'default'.
                // Retry once — the second call sometimes surfaces the real prompt.
                if (result === 'default') {
                    _warn('[Push] Got default (quiet prompt?), retrying...');
                    await new Promise(r => setTimeout(r, 300));
                    result = await Notification.requestPermission();
                    _log('[Push] Retry result:', result);
                }
            }
            setPermission(result);

            if (result !== 'granted') {
                if (result === 'denied') {
                    setError('Permissão negada. Clique no cadeado ao lado da URL e permita notificações.');
                } else {
                    // 'default' means the browser suppressed the prompt (quiet mode)
                    setError('QUIET_PROMPT');
                }
                return false;
            }

            setIsLoading(true);

            // 3) Register service worker
            _log('[Push] Registering service worker...');
            const swReg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
            _log('[Push] SW registered, scope:', swReg.scope);

            // 4) Wait for SW ready (with timeout)
            _log('[Push] Waiting for SW ready...');
            const registration = await swReadyWithTimeout(10000);
            _log('[Push] SW ready, state:', registration.active?.state);

            // 5) Get VAPID key
            _log('[Push] Fetching VAPID public key...');
            const vapidRes = await fetch(`${API_BASE}/vapid-public-key`, {
                credentials: 'include',
            });
            if (!vapidRes.ok) {
                const body = await vapidRes.text().catch(() => '');
                throw new Error(`Falha ao buscar chave VAPID (HTTP ${vapidRes.status}): ${body}`);
            }
            const { publicKey } = await vapidRes.json();
            if (!publicKey) throw new Error('Servidor retornou chave VAPID vazia — verifique VAPID_PUBLIC_KEY no servidor');
            _log('[Push] Got VAPID key, length:', publicKey.length);

            // 6) Subscribe via PushManager
            _log('[Push] Subscribing via PushManager...');
            let pushSub;
            try {
                pushSub = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(publicKey),
                });
            } catch (pushErr) {
                _err('[Push] pushManager.subscribe() failed:', pushErr.name, pushErr.message);
                // Do NOT set permission to 'denied' — this is a subscribe error,
                // not necessarily a browser permission issue.
                if (pushErr.name === 'NotAllowedError') {
                    setError(
                        'Falha ao ativar push: o navegador rejeitou a inscrição. '
                        + 'Tente recarregar a página e clicar novamente.'
                    );
                } else if (pushErr.name === 'InvalidStateError') {
                    setError('Já existe uma inscrição push ativa. Tente recarregar a página.');
                } else {
                    setError(`Erro ao inscrever no push: ${pushErr.message}`);
                }
                return false;
            }
            _log('[Push] PushManager subscription created');

            // 7) Register on backend
            _log('[Push] Sending subscription to server...');
            const res = await fetch(`${API_BASE}/push-subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    endpoint: pushSub.endpoint,
                    keys: {
                        p256dh: btoa(String.fromCharCode(...new Uint8Array(pushSub.getKey('p256dh')))),
                        auth: btoa(String.fromCharCode(...new Uint8Array(pushSub.getKey('auth')))),
                    },
                }),
            });
            if (!res.ok) {
                const body = await res.text().catch(() => '');
                throw new Error(`Falha ao registrar no servidor (HTTP ${res.status}): ${body}`);
            }

            setSubscription(pushSub);
            setIsSubscribed(true);
            _log('[Push] Inscrito com sucesso!');
            return true;
        } catch (err) {
            _err('[Push] Subscription failed:', err);
            setError(err.message || 'Falha ao ativar notificações push');
            return false;
        } finally {
            setIsLoading(false);
            busyRef.current = false;
        }
    }, []);

    /* ── unsubscribe ──────────────────────────────────────────── */
    const unsubscribe = useCallback(async () => {
        _log('[Push] unsubscribe() called');
        if (!subscription) {
            _log('[Push] No active subscription');
            return true;
        }
        if (busyRef.current) {
            _log('[Push] Already busy, ignoring');
            return false;
        }
        busyRef.current = true;
        setIsLoading(true);
        setError(null);

        try {
            await subscription.unsubscribe();
            await fetch(`${API_BASE}/push-unsubscribe`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ endpoint: subscription.endpoint }),
            });

            setSubscription(null);
            setIsSubscribed(false);
            _log('[Push] Desinscrito com sucesso');
            return true;
        } catch (err) {
            _err('[Push] Unsubscription failed:', err);
            setError(err.message);
            return false;
        } finally {
            setIsLoading(false);
            busyRef.current = false;
        }
    }, [subscription]);

    /* ── toggle ───────────────────────────────────────────────── */
    const toggleSubscription = useCallback(async () => {
        _log('[Push] toggle(), isSubscribed:', isSubscribed);
        return isSubscribed ? unsubscribe() : subscribe();
    }, [isSubscribed, subscribe, unsubscribe]);

    return {
        isSupported,
        isSubscribed,
        permission,
        isLoading,
        error,
        subscribe,
        unsubscribe,
        toggleSubscription,
        refreshPermission,
    };
}

export default usePushNotifications;
